import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ENV } from '@/config/env/env.read';
import beauthBrowser from '@/lib/beauth/beauth.browser';
import stepUpHandler from '@/lib/beauth/step.up.handler';

type TypeRetriable = InternalAxiosRequestConfig & { __authRetried?: boolean; __stepUpRetried?: boolean };

export const api = axios.create({
    baseURL: ENV.apiUrl,
    withCredentials: true,
    timeout: 20000,
});

const SESSION_PATHS = ['/auth/session/', '/auth/flows'];

const stringFields = (value: unknown): Record<string, string> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
};

export const refreshSession = async (): Promise<string | null> => {
    const outcome = await beauthBrowser.refresh(beauthBrowser.state().access === null ? 'resume' : 'unauthorized');
    return outcome.status === 'ok' ? outcome.access : null;
};

api.interceptors.request.use((config) => {
    const token = beauthBrowser.token();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ code?: unknown; fields?: unknown }>) => {
        const config = (error?.config ?? {}) as TypeRetriable;
        const status = error?.response?.status;
        const code = error?.response?.data?.code;
        const url = String(config.url ?? '');

        if (status === 401 && !config.__authRetried && !SESSION_PATHS.some((path) => url.startsWith(path))) {
            const token = await refreshSession();
            if (token) return api.request({ ...config, __authRetried: true } as TypeRetriable);
        }

        if (status === 403 && code === 'STEP_UP_REQUIRED' && !config.__stepUpRetried) {
            const confirmed = await stepUpHandler.ask(stringFields(error.response?.data?.fields));
            if (confirmed) return api.request({ ...config, __stepUpRetried: true } as TypeRetriable);
        }

        return Promise.reject(error);
    },
);

export default api;
